import { isEmpty, groupBy, sortBy } from "lodash";
import { grey } from "@mui/material/colors";
import { IAbsenceGroup } from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroups.models";
import {
  IAbsenceType,
  IAbsenceTypeListItem,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

export interface IAbsenceTypeGroup {
  groupId: string;
  groupName: string;
  groupColor: string;
  groupOrder: number;
  absenceTypes: IAbsenceTypeListItem[];
}

interface ITransformedData {
  active: IAbsenceTypeGroup[];
  inactive: IAbsenceTypeGroup[];
}

/**
 * Function transforming absence types data to structure ready for rendering in two lists (active and inactive),
 * grouped by absence group. It adds badge color from group config to absence type
 * and parent name if the absence type has a parent.
 * @param {array} absenceGroups - Collection of absence groups.
 * @param {array} absenceTypesData - Collection of absence types.
 */
export default (
  absenceGroups: IAbsenceGroup[] | undefined,
  absenceTypesData: IAbsenceType[] | undefined
): ITransformedData => {
  if (!absenceTypesData || isEmpty(absenceTypesData) || !absenceGroups) {
    return {
      active: [],
      inactive: [],
    };
  }

  // Create a map of group data by group ID
  const groupDataMap = Object.fromEntries(
    absenceGroups.map((group) => [
      group.id,
      { name: group.name, color: group.color, order: group.order },
    ])
  );

  // Create a map of absence type names by ID
  const absenceTypeNameMap = Object.fromEntries(
    absenceTypesData.map((type) => [type.id, type.name])
  );

  // Helper function to add color and parent name to absence type
  const enrichAbsenceType = (
    absenceType: IAbsenceType
  ): IAbsenceTypeListItem => {
    const parentAbsenceTypeId =
      absenceType.billingCycleConfiguration?.parentAbsenceTypeId;

    return {
      ...absenceType,
      color: absenceType.active
        ? groupDataMap[absenceType.absenceGroupId]?.color || "#C6D9BE"
        : grey[500],
      parentName: parentAbsenceTypeId
        ? absenceTypeNameMap[parentAbsenceTypeId] || ""
        : "",
    };
  };

  // Helper function to sort absence types with parent-child relationship
  const sortAbsenceTypes = (
    items: IAbsenceTypeListItem[]
  ): IAbsenceTypeListItem[] => {
    const sorted: IAbsenceTypeListItem[] = [];

    // Separate parents and children
    const { true: children = [], false: parents = [] } = groupBy(
      items,
      (item) => !!item.billingCycleConfiguration?.parentAbsenceTypeId
    );

    // Group children by their parent ID
    const childrenByParent = groupBy(
      children,
      (item) => item.billingCycleConfiguration?.parentAbsenceTypeId
    );

    // Add each parent followed by its children
    parents.forEach((parent) => {
      sorted.push(parent);
      const parentChildren = childrenByParent[parent.id] || [];
      sorted.push(...parentChildren);
    });

    return sorted;
  };

  // Helper function to group items by absence group and return sorted array
  const groupByAbsenceGroup = (
    items: IAbsenceTypeListItem[]
  ): IAbsenceTypeGroup[] => {
    const grouped = groupBy(items, "absenceGroupId");

    const groupedArray = Object.entries(grouped).map(
      ([groupId, groupItems]) => {
        const groupData = groupDataMap[groupId];

        return {
          groupId,
          groupName: groupData?.name || "",
          groupColor: groupData?.color || "#C6D9BE",
          groupOrder: groupData?.order || 0,
          absenceTypes: sortAbsenceTypes(groupItems),
        };
      }
    );

    // Sort groups by order
    return sortBy(groupedArray, "groupOrder");
  };

  // Process active and inactive items
  const activeItems = absenceTypesData
    .filter((type) => type.active)
    .map(enrichAbsenceType);

  const inactiveItems = absenceTypesData
    .filter((type) => !type.active)
    .map(enrichAbsenceType);

  return {
    active: groupByAbsenceGroup(activeItems),
    inactive: groupByAbsenceGroup(inactiveItems),
  };
};
